import { useGetClusterInfiniteQuery } from "@/components/ContentTabs/clustersTab/ClusterTableLayout/hook/useGetClusterInfiniteQuery"
import { NextStepButton } from "@/components/FormButtons/NextStepButton"
import { RequiredErrorMessage } from "@/components/RequiredErrorMessage"
import SingleField from "@/components/Sections/SingleField"
import { useStepperContext } from "@/components/Stepper/StepperContext"
import { P2 } from "@/components/Typographies"
import { ClusterStatus } from "@/domain/cluster/constants"
import { getDefaultRelatedCluster } from "@/domain/cluster/getRelatedCluster"
import { getDisplayedName } from "@/domain/people/getDisplayedName"
import useGetUserInfos from "@/hooks/useGetUserInfos"
import type { ClusterJsonldReadItemClusterItem, PeopleJsonldReadItemPeople } from "@/networking/api"
import { useGetPeopleInfiniteQuery } from "@/pages/People/components/PeopleTableLayout/hook/useGetPeopleInfiniteQuery"
import { useGetReactSelectStyles } from "@/theme/components-style-overrides/reactselect-definition"
import { Orders } from "@/utils/filters/types"
import { isBlank, isNotBlank, isNotNullNorUndefined } from "@/utils/ts-utils"
import { Box, Divider, Flex, FormControl, Textarea } from "@chakra-ui/react"
import { useMemo } from "react"
import { Controller, useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import ReactSelect from "react-select"
import AddClientButton from "../../components/AddClientButton"
import { useCreationMultiStepFormContext } from "../../context/ShareRequestInterbuCreateModalContext"
import { CLIENT_SEARCH_MIN_CHARS, getClientSearchMessageId } from "../../helpers/utils"

/** Server-side search already filters options; skip client label matching (breaks UUID search).
const acceptAllServerFilteredOptions = () => true
const acceptAllServerFilteredOptions = createFilter({
  ignoreCase: true,
  ignoreAccents: true,
  matchFrom: "any",
  trim: true,
})
*/

type StepForm = {
  people_id: string
  people_comment?: string | null
  from_cluster_id: string
}

export default () => {
  const intl = useIntl()
  const { goToNextStep } = useStepperContext()

  const { clusterIds, data: userInfos } = useGetUserInfos()
  // Without an agency, the user cannot create an InterBU: an empty "id[]" would list them all.
  const hasNoCluster = isNotNullNorUndefined(userInfos) && clusterIds.length === 0

  const {
    formState,
    setFormState,
    sharedEntity,
    storeValues,
    setValueSelected,
    storeSearchTerms,
    setSearchTerm,
  } = useCreationMultiStepFormContext()

  const peopleSelected = useMemo(() => {
    return (storeValues?.people_id?.[0] ?? sharedEntity ?? undefined) as PeopleJsonldReadItemPeople
  }, [storeValues, sharedEntity])
  const peopleSelectedClusterName = useMemo(
    () => getDefaultRelatedCluster(peopleSelected?.cluster)?.name,
    [peopleSelected]
  )
  const peopleSelectedName = useMemo(() => getDisplayedName(peopleSelected), [peopleSelected])

  const hasPeople = useMemo(() => isNotNullNorUndefined(sharedEntity), [sharedEntity])
  const hasPeopleSelected = useMemo(() => isNotNullNorUndefined(peopleSelected), [peopleSelected])

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StepForm>({
    defaultValues: {
      people_id: formState.people_id,
      people_comment: formState.people_comment,
      from_cluster_id: formState.from_cluster_id,
    },
  })

  // Peoples request / options

  const searchTermClient = useMemo(() => storeSearchTerms.people_id ?? "", [storeSearchTerms])

  const { people, isFetchingData: isLoadingPeople } = useGetPeopleInfiniteQuery(
    {
      search: searchTermClient,
      // "cluster.cluster.id[]": [...(clusterIds ?? []), NIL_UUID],
      isShared: true,
      itemsPerPage: 30,
      withMinProps: true,
      partial: true,
      withClustersMinProps: true,
    },
    undefined,
    { enabled: !hasPeople && searchTermClient.length >= CLIENT_SEARCH_MIN_CHARS }
  )
  const peopleOptions = useMemo(
    () =>
      (people ?? []).map(p => {
        return {
          value: p,
          label: `${getDisplayedName(p)} (${getDefaultRelatedCluster(p.cluster)?.name ?? "unknown"})`,
        }
      }),
    [people]
  )
  const peopleOptionSelected = useMemo(
    () =>
      isNotNullNorUndefined(peopleSelected)
        ? {
            value: peopleSelected,
            label: `${getDisplayedName(peopleSelected)} (${getDefaultRelatedCluster(peopleSelected?.cluster)?.name ?? "unknown"})`,
          }
        : undefined,
    [peopleSelected]
  )

  let updateClientSearchItv: NodeJS.Timeout
  const onPeopleInputChange = (text: string) => {
    if (searchTermClient === text) return
    if (isNotNullNorUndefined(updateClientSearchItv)) clearTimeout(updateClientSearchItv)
    updateClientSearchItv = setTimeout(() => setSearchTerm("people_id", text), 500)
  }

  // Clusters request / options

  const searchTermCluster = useMemo(() => storeSearchTerms.fromCluster ?? "", [storeSearchTerms])

  const { clusters, isFetchingData: isLoadingClusters } = useGetClusterInfiniteQuery(
    {
      search: searchTermCluster,
      itemsPerPage: 20,
      "id[]": clusterIds,
      "status.code": ClusterStatus.ACTIVE,
      "order[name]": Orders.ASC,
      withMinProps: true,
    },
    { enabled: clusterIds.length > 0 }
  )
  const clusterOptions = useMemo(
    () =>
      clusters?.map(c => {
        return {
          value: c,
          label: c.name ?? "unknown",
        }
      }),
    [clusters]
  )
  const hasSingleCluster = useMemo(
    () => isBlank(searchTermCluster) && clusterOptions.length === 1,
    [searchTermCluster, clusterOptions]
  )
  const firstCluster = useMemo(() => clusters?.[0], [clusters])
  const clusterOptionSelected = useMemo(() => {
    const clusterSelected = (storeValues?.fromCluster?.[0] ?? undefined) as
      | ClusterJsonldReadItemClusterItem
      | undefined
    return isNotNullNorUndefined(clusterSelected)
      ? {
          value: clusterSelected,
          label: clusterSelected.name ?? "unknown",
        }
      : undefined
  }, [storeValues])

  let updateClusterSearchItv: NodeJS.Timeout
  const onClusterInputChange = (text: string) => {
    if (searchTermCluster === text) return
    if (isNotNullNorUndefined(updateClusterSearchItv)) clearTimeout(updateClusterSearchItv)
    updateClusterSearchItv = setTimeout(() => setSearchTerm("fromCluster", text), 500)
  }

  // onSubmit

  const onSubmit = (stepFormData: StepForm) => {
    setFormState(prevFormState => ({
      ...prevFormState,
      ...stepFormData,
      from_cluster_id: (hasSingleCluster ? firstCluster?.id : stepFormData.from_cluster_id) ?? "",
    }))

    goToNextStep()
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} maxW="100%" margin="auto">
      <Flex direction="column" gap="5" my="8">
        {/* The sender agency first: it only depends on the user, not on the client. */}
        <SingleField
          label={intl.formatMessage({ id: "userSharedData.interbu.modal.steps.0.fromCluster" })}
          required
        >
          {hasNoCluster ? (
            <P2 data-testid="interbu-no-sender-agency">
              <FormattedMessage id="userSharedData.interbu.modal.steps.0.noCluster" />
            </P2>
          ) : hasSingleCluster ? (
            (firstCluster?.name ?? "-")
          ) : (
            <FormControl isInvalid={!!errors.from_cluster_id}>
              <Controller
                control={control}
                name="from_cluster_id"
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, ref }, fieldState }) => (
                  <ReactSelect
                    ref={ref}
                    onBlur={onBlur}
                    // filterOption={acceptAllServerFilteredOptions}
                    options={clusterOptions}
                    defaultValue={clusterOptionSelected}
                    onChange={newVal => {
                      onChange(newVal?.value?.id)
                      setValueSelected("fromCluster", newVal)
                    }}
                    onInputChange={onClusterInputChange}
                    styles={useGetReactSelectStyles(fieldState, { width: "100%" })}
                    isLoading={isLoadingClusters}
                    loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                  />
                )}
              />
              <RequiredErrorMessage />
            </FormControl>
          )}
        </SingleField>
        <Divider />
        <SingleField
          label={intl.formatMessage({ id: "userSharedData.interbu.modal.steps.0.people" })}
          required
        >
          <Flex direction="column" gap="2" width="100%">
            {hasPeople && <P2>{peopleSelectedName}</P2>}
            {!hasPeople && (
              <FormControl isInvalid={!!errors.people_id}>
                <Controller
                  control={control}
                  name="people_id"
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, ref }, fieldState }) => (
                    <ReactSelect
                      ref={ref}
                      onBlur={onBlur}
                      placeholder={intl.formatMessage({
                        id: "userSharedData.interbu.modal.steps.0.select",
                      })}
                      // filterOption={acceptAllServerFilteredOptions}
                      options={peopleOptions}
                      defaultValue={peopleOptionSelected}
                      onChange={newVal => {
                        onChange(newVal?.value?.id ?? "")
                        setValueSelected("people_id", newVal)
                      }}
                      onInputChange={onPeopleInputChange}
                      isClearable={true}
                      styles={useGetReactSelectStyles(fieldState, { width: "100%" })}
                      isLoading={isLoadingPeople}
                      loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                      noOptionsMessage={({ inputValue }) =>
                        intl.formatMessage({
                          id: getClientSearchMessageId(inputValue, searchTermClient),
                        })
                      }
                    />
                  )}
                />
                <RequiredErrorMessage />
              </FormControl>
            )}
            {!hasPeopleSelected && <AddClientButton />}
          </Flex>
        </SingleField>
        {isNotBlank(peopleSelectedClusterName) && (
          <SingleField
            label={intl.formatMessage({
              id: "userSharedData.interbu.modal.steps.0.peopleCluster",
            })}
          >
            <Flex direction="column" gap="2" width="100%">
              <P2>{peopleSelectedClusterName}</P2>
            </Flex>
          </SingleField>
        )}
        <SingleField
          label={intl.formatMessage({ id: "userSharedData.interbu.modal.steps.0.description" })}
          justifyContent="start"
        >
          <Textarea {...register("people_comment")} />
        </SingleField>
      </Flex>

      <Flex justifyContent="center" mt="100px" my={10}>
        <NextStepButton isDisabled={hasNoCluster}>
          <FormattedMessage id="userSharedData.interbu.modal.nextStep" />
        </NextStepButton>
      </Flex>
    </Box>
  )
}
